import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {Pill} from '@wandb/weave/components/Tag';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {components, ControlProps, OptionProps} from 'react-select';

import {Messages} from '../../../ChatView/types';
import {WEAVE_REF_SCHEME} from '../../../wfReactInterface/constants';
import {objectVersionKeyToRefUri} from '../../../wfReactInterface/utilities';
import {
  OpenDirection,
  Position,
  SearchableMenu,
  SubMenu,
  SubMenuItem,
} from '../dropdownComponents';
import {PromptOption, PromptVersion} from './usePromptOptions';

interface PromptDropdownProps {
  entity: string;
  project: string;
  value?: string;
  onChange: (promptName: string, messages: Messages, promptRef: string) => void;
  className?: string;
  promptOptions: PromptOption[];
  loading?: boolean;
}

// Submenu component that displays versions
const PromptVersionsSubMenu: React.FC<{
  versions: PromptVersion[];
  position: Position;
  onSelect: (version: PromptVersion) => void;
}> = ({versions, position, onSelect}) => {
  const direction: OpenDirection = {horizontal: 'right'};

  return (
    <SubMenu position={position} direction={direction} width="250px">
      {versions.map(version => (
        <SubMenuItem
          key={version.value}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onSelect(version);
          }}>
          <span>v{version.versionIndex}</span>
          {version.isLatest && <Pill label="Latest" color="moon" />}
        </SubMenuItem>
      ))}
    </SubMenu>
  );
};

// Custom option component with hover submenu
const PromptOptionWithSubmenu: React.FC<
  OptionProps<PromptOption, false> & {
    onVersionSelect: (version: PromptVersion) => void;
  }
> = props => {
  const {onVersionSelect, data} = props;
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState<Position>({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  });
  const optionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHovered && optionRef.current) {
      const rect = optionRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
      });
    }
  }, [isHovered]);

  const handleVersionSelect = useCallback(
    (version: PromptVersion) => {
      onVersionSelect(version);
      props.selectProps.onMenuClose?.();
    },
    [onVersionSelect, props.selectProps]
  );

  const hasVersions = data.versions.length > 0;

  return (
    <div
      ref={optionRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative w-full">
      <div className="relative z-10">
        <components.Option {...props}>
          <div className="flex items-center justify-between w-full">
            <span>{data.label}</span>
            {hasVersions && <Icon name="chevron-next" color="moon_500" />}
          </div>
        </components.Option>
      </div>
      {isHovered && hasVersions && (
        <PromptVersionsSubMenu
          versions={data.versions}
          position={position}
          onSelect={handleVersionSelect}
        />
      )}
    </div>
  );
};

// Search option component (shown when searching)
const SearchResultOption: React.FC<
  OptionProps<PromptOption, false> & {
    onVersionSelect: (version: PromptVersion) => void;
    searchTerm: string;
  }
> = props => {
  const {data, searchTerm, onVersionSelect} = props;

  // Filter versions that match the search
  const filteredVersions = data.versions.filter(
    version =>
      version.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `v${version.versionIndex}`.includes(searchTerm)
  );

  if (filteredVersions.length === 0) return null;

  return (
    <div>
      <div className="px-12 pt-4 text-moon-800 font-semibold cursor-default">
        {data.label}
      </div>
      <div className="px-4">
        {filteredVersions.map(version => (
          <div
            key={version.value}
            onClick={() => {
              onVersionSelect(version);
              props.selectProps.onMenuClose?.();
            }}
            className="px-12 py-8 cursor-pointer rounded flex justify-between items-center hover:bg-moon-100">
            <span>{version.label}</span>
            {version.isLatest && <Pill label="Latest" color="moon" />}
          </div>
        ))}
      </div>
    </div>
  );
};

// Custom control component (button trigger)
const PromptDropdownTrigger: React.FC<
  ControlProps<PromptOption, false> & {
    selectedVersionLabel?: string;
    onToggleMenu?: () => void;
  }
> = props => {
  const {hasValue, getValue, selectedVersionLabel, onToggleMenu} = props;
  const selectedOption = hasValue ? getValue()[0] : null;
  const displayText =
    selectedVersionLabel || selectedOption?.label || 'Select a prompt';

  return (
    <div>
      <Button
        variant="secondary"
        icon="forum-chat-bubble"
        endIcon="chevron-down"
        size="small"
        onClick={onToggleMenu || (() => {})}>
        {displayText}
      </Button>
      <div className="hidden">
        <components.Control {...props} />
      </div>
    </div>
  );
};

export const PromptDropdown: React.FC<PromptDropdownProps> = ({
  entity,
  project,
  value,
  onChange,
  className,
  promptOptions,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Clear search term when menu closes
  const handleMenuClose = useCallback(() => {
    setMenuIsOpen(false);
    setSearchTerm('');
  }, []);

  const handleMenuOpen = useCallback(() => {
    setMenuIsOpen(true);
  }, []);

  const handleToggleMenu = useCallback(() => {
    setMenuIsOpen(prev => !prev);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuIsOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        handleMenuClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuIsOpen, handleMenuClose]);

  const handleVersionSelect = useCallback(
    (version: PromptVersion) => {
      // Build proper weave URI using helper function
      const objectId = version.value.split(':')[0]; // Extract "physics" from "physics:v0"
      const promptRef = objectVersionKeyToRefUri({
        scheme: WEAVE_REF_SCHEME,
        entity,
        project,
        weaveKind: 'object',
        objectId,
        versionHash: version.digest,
        path: '',
        refExtra: '',
      });
      onChange(version.value, version.messages, promptRef);
    },
    [onChange, entity, project]
  );

  const handlePromptSelect = useCallback(
    (selectedOption: PromptOption | null) => {
      if (!selectedOption) {
        onChange('', [], '');
        return;
      }

      // When clicking on a prompt (not a version), select the latest version
      if (selectedOption.latestVersion) {
        const objectId = selectedOption.latestVersion.value.split(':')[0];
        const promptRef = objectVersionKeyToRefUri({
          scheme: WEAVE_REF_SCHEME,
          entity,
          project,
          weaveKind: 'object',
          objectId,
          versionHash: selectedOption.latestVersion.digest,
          path: '',
          refExtra: '',
        });
        onChange(
          selectedOption.latestVersion.value,
          selectedOption.latestVersion.messages,
          promptRef
        );
      }
    },
    [onChange, entity, project]
  );

  const selectedOption = useMemo(() => {
    if (!value) return null;

    // Find the option that contains a version matching the value
    for (const option of promptOptions) {
      const matchingVersion = option.versions.find(v => v.value === value);
      if (matchingVersion) {
        return option;
      }
    }
    return null;
  }, [value, promptOptions]);

  const selectedVersionLabel = useMemo(() => {
    if (!value || !selectedOption) return undefined;

    // Find the specific version that matches the value
    const matchingVersion = selectedOption.versions.find(
      v => v.value === value
    );
    return matchingVersion?.label;
  }, [value, selectedOption]);

  return (
    <div className={className} ref={dropdownRef}>
      <Select
        isDisabled={loading}
        placeholder={loading ? 'Loading prompts...' : 'Select a prompt'}
        value={selectedOption}
        onChange={handlePromptSelect}
        options={promptOptions}
        size="medium"
        isSearchable={false}
        menuIsOpen={menuIsOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        components={{
          Control: props => (
            <PromptDropdownTrigger
              {...props}
              selectedVersionLabel={selectedVersionLabel}
              onToggleMenu={handleToggleMenu}
            />
          ),
          Menu: props => (
            <SearchableMenu
              {...props}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search for prompt"
            />
          ),
          Option: props => {
            // Show search results when searching, otherwise show submenu
            if (searchTerm) {
              return (
                <SearchResultOption
                  {...props}
                  onVersionSelect={handleVersionSelect}
                  searchTerm={searchTerm}
                />
              );
            }
            return (
              <PromptOptionWithSubmenu
                {...props}
                onVersionSelect={handleVersionSelect}
              />
            );
          },
        }}
        filterOption={(option, inputValue) => {
          // Filter based on our custom search term
          if (!searchTerm) return true;
          const searchLower = searchTerm.toLowerCase();
          return (
            option.data.label.toLowerCase().includes(searchLower) ||
            option.data.versions.some(v =>
              v.label.toLowerCase().includes(searchLower)
            )
          );
        }}
        maxMenuHeight={500}
      />
    </div>
  );
};
